import React from 'react';
import { createHashRouter, createRoutesFromElements, RouterProvider, Route, Navigate, Outlet } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Spinner, Container } from 'react-bootstrap';
import Navigation from './components/Navigation';
import Login from './pages/Login';
import Home from './pages/Home';
import Companies from './pages/Companies';
import CompanyDetail from './pages/CompanyDetail';
import CRMCompanyDetail from './pages/CRMCompanyDetail';
import OfficeDetail from './pages/OfficeDetail';
import UserDetail from './pages/UserDetail';
import Reports from './pages/Reports';
import UserSignups from './pages/UserSignups';
import Profile from './pages/Profile';
import Monitoring from './pages/Monitoring';
import Logs from './pages/Logs';
import ServerHealth from './pages/ServerHealth';
import SystemReport from './pages/SystemReport';
import CacheControls from './pages/CacheControls';
import UserSearch from './pages/UserSearch';
import RecentCompanyNotes from './pages/RecentCompanyNotes';
import PendingApprovals from './pages/PendingApprovals';
import SharedEmailDomains from './pages/SharedEmailDomains';
import SignupConversion from './pages/SignupConversion';
import EngagementReport from './pages/EngagementReport';
import CompanyAdmins from './pages/CompanyAdmins';
import UnregisteredUserVisits from './pages/UnregisteredUserVisits';
import MarketingSite from './pages/MarketingSite';
import MarketingSiteEditor from './pages/marketing-site/MarketingSiteEditor';
import DataInsights from './pages/DataInsights';
import OfficeWeekdays from './pages/insights/OfficeWeekdays';
import WhereaboutsSplit from './pages/insights/WhereaboutsSplit';
import SplitDays from './pages/insights/SplitDays';
import DeskLoyalty from './pages/insights/DeskLoyalty';
import MeetingLeadTime from './pages/insights/MeetingLeadTime';
import MeetingLengths from './pages/insights/MeetingLengths';
import HolidayPatterns from './pages/insights/HolidayPatterns';
import WorkplaceHeroes from './pages/insights/WorkplaceHeroes';
import VisitorWeekdays from './pages/insights/VisitorWeekdays';
import ParkingWeekdays from './pages/insights/ParkingWeekdays';
import CustomStatusLabels from './pages/insights/CustomStatusLabels';
import ProtectedRoute from './components/ProtectedRoute';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext';
import { PendingApprovalsProvider } from './context/PendingApprovalsContext';

// The shell around every page: the nav once signed in, then whichever page is open
const RootLayout = () => {
  const { accounts } = useMsal();
  const { accessDenied } = usePermissions();
  return (
    <>
      {accounts.length > 0 && !accessDenied && <Navigation />}
      <Outlet />
    </>
  );
};

// A data router, so a page holding unsaved edits can block in-app navigation with useBlocker
const router = createHashRouter(createRoutesFromElements(
  <Route element={<RootLayout />}>
    <Route path="/login" element={<Login />} />
    <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
    <Route
      path="/companies"
      element={
        <ProtectedRoute>
          <Companies />
        </ProtectedRoute>
      }
    />
    <Route
      path="/companies/:id"
      element={
        <ProtectedRoute>
          <CompanyDetail />
        </ProtectedRoute>
      }
    />
    <Route
      path="/offices/:id"
      element={
        <ProtectedRoute>
          <OfficeDetail />
        </ProtectedRoute>
      }
    />
    <Route
      path="/users/:id"
      element={
        <ProtectedRoute>
          <UserDetail />
        </ProtectedRoute>
      }
    />
    <Route
      path="/reports"
      element={
        <ProtectedRoute>
          <Reports />
        </ProtectedRoute>
      }
    />
    <Route
      path="/recent-notes"
      element={
        <ProtectedRoute>
          <RecentCompanyNotes />
        </ProtectedRoute>
      }
    />
    <Route
      path="/approvals"
      element={
        <ProtectedRoute>
          <PendingApprovals />
        </ProtectedRoute>
      }
    />
    <Route
      path="/reports/shared-email-domains"
      element={
        <ProtectedRoute>
          <SharedEmailDomains />
        </ProtectedRoute>
      }
    />
    <Route
      path="/reports/signup-conversion"
      element={
        <ProtectedRoute>
          <SignupConversion />
        </ProtectedRoute>
      }
    />
    <Route
      path="/reports/engagement"
      element={
        <ProtectedRoute>
          <EngagementReport />
        </ProtectedRoute>
      }
    />
    <Route
      path="/reports/company-admins"
      element={
        <ProtectedRoute>
          <CompanyAdmins />
        </ProtectedRoute>
      }
    />
    <Route
      path="/user-signups"
      element={
        <ProtectedRoute>
          <UserSignups />
        </ProtectedRoute>
      }
    />
    <Route
      path="/system-report"
      element={
        <ProtectedRoute>
          <SystemReport />
        </ProtectedRoute>
      }
    />
    <Route
      path="/monitoring"
      element={
        <ProtectedRoute>
          <Monitoring />
        </ProtectedRoute>
      }
    />
    <Route
      path="/logs"
      element={
        <ProtectedRoute>
          <Logs />
        </ProtectedRoute>
      }
    />
    <Route
      path="/server-health"
      element={
        <ProtectedRoute>
          <ServerHealth />
        </ProtectedRoute>
      }
    />
    <Route
      path="/user-search"
      element={
        <ProtectedRoute>
          <UserSearch />
        </ProtectedRoute>
      }
    />
    <Route
      path="/unregistered-user-visits"
      element={
        <ProtectedRoute>
          <UnregisteredUserVisits />
        </ProtectedRoute>
      }
    />
    <Route
      path="/marketing-site"
      element={
        <ProtectedRoute>
          <MarketingSiteEditor />
        </ProtectedRoute>
      }
    />
    <Route
      path="/marketing-site/agent"
      element={
        <ProtectedRoute>
          <MarketingSite />
        </ProtectedRoute>
      }
    />
    <Route
      path="/cache-controls"
      element={
        <ProtectedRoute>
          <CacheControls />
        </ProtectedRoute>
      }
    />
    <Route
      path="/profile"
      element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      }
    />
    <Route
      path="/crm"
      element={
        <ProtectedRoute>
          <Companies />
        </ProtectedRoute>
      }
    />
    <Route
      path="/crm/companies/:id"
      element={
        <ProtectedRoute>
          <CRMCompanyDetail />
        </ProtectedRoute>
      }
    />
    <Route path="/data-insights" element={<ProtectedRoute><DataInsights /></ProtectedRoute>} />
    <Route path="/data-insights/office-weekdays" element={<ProtectedRoute><OfficeWeekdays /></ProtectedRoute>} />
    <Route path="/data-insights/whereabouts-split" element={<ProtectedRoute><WhereaboutsSplit /></ProtectedRoute>} />
    <Route path="/data-insights/split-days" element={<ProtectedRoute><SplitDays /></ProtectedRoute>} />
    <Route path="/data-insights/desk-loyalty" element={<ProtectedRoute><DeskLoyalty /></ProtectedRoute>} />
    <Route path="/data-insights/meeting-lead-time" element={<ProtectedRoute><MeetingLeadTime /></ProtectedRoute>} />
    <Route path="/data-insights/meeting-lengths" element={<ProtectedRoute><MeetingLengths /></ProtectedRoute>} />
    <Route path="/data-insights/holiday-patterns" element={<ProtectedRoute><HolidayPatterns /></ProtectedRoute>} />
    <Route path="/data-insights/workplace-heroes" element={<ProtectedRoute><WorkplaceHeroes /></ProtectedRoute>} />
    <Route path="/data-insights/visitor-weekdays" element={<ProtectedRoute><VisitorWeekdays /></ProtectedRoute>} />
    <Route path="/data-insights/parking-weekdays" element={<ProtectedRoute><ParkingWeekdays /></ProtectedRoute>} />
    <Route path="/data-insights/custom-labels" element={<ProtectedRoute><CustomStatusLabels /></ProtectedRoute>} />
    <Route path="*" element={<Navigate to="/" />} />
  </Route>
));

function App() {
  const { inProgress } = useMsal();

  if (inProgress !== InteractionStatus.None) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <PermissionsProvider>
      <PendingApprovalsProvider>
        <RouterProvider router={router} />
      </PendingApprovalsProvider>
    </PermissionsProvider>
  );
}

export default App;
